import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../utils/helpers/hydration';

export interface ListItem {
  text: string;
  icon?: string;
}

/**
 * @example
 *   <me-list items='[{"text": "Unlimited storage"}, {"text": "Priority support"}, {"text": "Advanced analytics"}]'></me-list>
 */
@Component({
  tag: 'me-list',
  styleUrl: 'index.scss',
  shadow: false,
})
export class MeList {
  /**
   * Array of list items with optional icons
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} text - List item text
   * @item.property {string} icon - Icon name (optional)
   * @item.required text
   * @item.content text
   */
  @Prop({ mutable: true }) items: ListItem[] | string = [];

  /**
   * Display list inline or in column
   * @default 'column'
   */
  @Prop() layout: 'inline' | 'column' = 'column';

  /**
   * List style type
   * @default 'plain'
   */
  @Prop() listType: 'plain' | 'icon' | 'numbered' = 'plain';

  /**
   * Default icon to use when listType is 'icon' and no specific icon provided
   */
  @Prop() defaultIcon?: string;

  /**
   * Inline icons during server-side rendering (SSR).
   * When true, icons are fetched from CDN on the server and inlined into HTML.
   * Only affects SSR - improves LCP for above-the-fold lists.
   */
  @Prop() iconInline?: boolean = false;

  componentWillLoad() {
    this.items = parseJsonProp(this.items, []);
  }

  render() {
    const items = Array.isArray(this.items) ? this.items : [];
    const classes = {
      'me-list': true,
      [`me-list--${this.layout}`]: true,
      [`me-list--${this.listType}`]: true,
    };

    const renderListItem = (item: ListItem, index: number) => {
      return (
        <li class="me-list__item">
          {this.listType === 'icon' && <me-icon name={item.icon || this.defaultIcon || 'interface--check'} class="me-list__icon" inline={this.iconInline}></me-icon>}
          {this.listType === 'numbered' && <span class="me-list__number">{index + 1}.</span>}
          <span class="me-list__text">{item.text}</span>
        </li>
      );
    };

    return <ul class={classes}>{items.map((item, index) => renderListItem(item, index))}</ul>;
  }
}
